import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import imgHeroProtection from "../../../Main/Landing/images/Vault/hero-protection.jpg";
import iconVerifiedCommunity from "../../../Main/Landing/images/Vault/verified-community.png";
import iconSecurePlatform from "../../../Main/Landing/images/Vault/secure-platform.png";
import iconProtectionOfUser from "../../../Main/Landing/images/Vault/protection-of-user.png";
import iconPrivateInteraction from "../../../Main/Landing/images/Vault/private-interaction.png";
import iconDiscreetBrowsing from "../../../Main/Landing/images/Vault/discreet-browsing.png";

const useStyles = makeStyles((theme) => ({
    privacySection: {
        padding: '60px 5%',
        backgroundColor: '#111111',
        [theme.breakpoints.down('sm')]: {
            padding: '40px 5%',
        }
    },
    privacyContainer: {
        maxWidth: 1314,
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    privacyTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: '52px',
        fontWeight: 400,
        lineHeight: '63px',
        color: '#ffffff',
        marginBottom: '35px',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '36px',
            lineHeight: '44px',
            textAlign: 'center',
        }
    },
    privacyGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '30px',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
            gap: '24px',
        }
    },
    privacyColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '30px',
        [theme.breakpoints.down('sm')]: {
            gap: '24px',
        }
    },
    privacyCard: {
        backgroundColor: '#1E1E1E',
        borderRadius: '12px',
        padding: '24px 20px',
        textAlign: 'left',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    privacyIconPlaceholder: {
        marginBottom: '20px',
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    privacyIcon: {
        width: 48,
        height: 48,
        objectFit: 'contain',
    },
    privacyCardTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: '24px',
        fontWeight: 500,
        lineHeight: '100%',
        color: '#ffffff',
        marginBottom: '12px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '20px',
            marginBottom: '8px',
        }
    },
    privacyCardDesc: {
        fontSize: '18px',
        fontWeight: 200,
        color: '#ffffff',
        lineHeight: '27px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '16px',
            lineHeight: '24px',
        }
    },
    privacyCenterCard: {
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        height: '100%',
        minHeight: '450px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        textAlign: 'left',
        [theme.breakpoints.down('sm')]: {
            minHeight: '450px',
        }
    },
    privacyCenterImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        zIndex: 0,
    },
    privacyCenterOverlay: {
        position: 'relative',
        zIndex: 1,
        padding: '40px 32px 32px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 50%, transparent 100%)',
    }
}));

const PRIVACY_FEATURES = {
    left: [
        { title: "Verified community", desc: "Every member and creator passes identity checks before joining.", icon: iconVerifiedCommunity },
        { title: "Secure platform", desc: "Payments and messages are protected end to end.", icon: iconSecurePlatform }
    ],
    right: [
        { title: "Private interactions", desc: "Conversations with creators stay between you and them.", icon: iconPrivateInteraction },
        { title: "Discreet browsing experience", desc: "Nothing about your activity is visible to anyone but you.", icon: iconDiscreetBrowsing }
    ],
    center: {
        title: "Protection of user information",
        desc: "Your personal data is never sold or shared with third parties.",
        image: imgHeroProtection,
        icon: iconProtectionOfUser
    }
};

export const VaultPrivacySection: React.FC = () => {
    const classes = useStyles();

    return (
        <Box className={classes.privacySection}>
            <Box className={classes.privacyContainer}>
                <Typography variant="h2" className={classes.privacyTitle}>
                    Privacy & Discretion
                </Typography>

                <Box className={classes.privacyGrid}>
                    {/* Left Column */}
                    <Box className={classes.privacyColumn}>
                        {PRIVACY_FEATURES.left.map((feature, index) => (
                            <Box key={index} className={classes.privacyCard}>
                                <Box className={classes.privacyIconPlaceholder}>
                                    <img src={feature.icon} alt={feature.title} className={classes.privacyIcon} />
                                </Box>
                                <Typography className={classes.privacyCardTitle}>{feature.title}</Typography>
                                <Typography className={classes.privacyCardDesc}>{feature.desc}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Center Column */}
                    <Box className={classes.privacyCenterCard}>
                        <img src={PRIVACY_FEATURES.center.image} alt={PRIVACY_FEATURES.center.title} className={classes.privacyCenterImage} />
                        <Box className={classes.privacyCenterOverlay}>
                            <Box className={classes.privacyIconPlaceholder}>
                                <img src={PRIVACY_FEATURES.center.icon} alt={PRIVACY_FEATURES.center.title} className={classes.privacyIcon} />
                            </Box>
                            <Typography className={classes.privacyCardTitle}>{PRIVACY_FEATURES.center.title}</Typography>
                            <Typography className={classes.privacyCardDesc}>{PRIVACY_FEATURES.center.desc}</Typography>
                        </Box>
                    </Box>

                    {/* Right Column */}
                    <Box className={classes.privacyColumn}>
                        {PRIVACY_FEATURES.right.map((feature, index) => (
                            <Box key={index} className={classes.privacyCard}>
                                <Box className={classes.privacyIconPlaceholder}>
                                    <img src={feature.icon} alt={feature.title} className={classes.privacyIcon} />
                                </Box>
                                <Typography className={classes.privacyCardTitle}>{feature.title}</Typography>
                                <Typography className={classes.privacyCardDesc}>{feature.desc}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
